import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { mdiHandHeart } from '@mdi/js';
import BaseModal from './BaseModal';
import { paymentApi } from '../api/payment';
import { formatCurrencyFromMinor } from '../utils/currency';
import './TipModal.css';

// Tip amounts in integer cents. Bounds mirror the backend (payment/tip.service.js).
const AMOUNT_PRESETS = [200, 500, 1000, 2000];
const MIN_TIP = 100;
const MAX_TIP = 100000;

/**
 * "Help us out" modal: a gift to Dance Localé (a "tip" in the backend), opened from a
 * mascot (demo) instructor's profile or the end of the demo booking. Not a booking.
 * Signed-in visitors choose an amount and go straight to the /tip/:token payment page;
 * guests are asked to log in (gifts are signed-in only).
 */
const TipModal = ({ isOpen, onClose, instructorId, currency, isAuthenticated }) => {
    const navigate = useNavigate();
    const [preset, setPreset] = useState(null);
    const [custom, setCustom] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Always mounted by the profile page, so reset each time it opens.
    useEffect(() => {
        if (isOpen) {
            setPreset(null);
            setCustom('');
        }
    }, [isOpen]);

    const amount = preset === 'other' ? Math.round((parseFloat(custom) || 0) * 100) : preset;
    const isValid = amount !== null && amount >= MIN_TIP && amount <= MAX_TIP;

    const handleGive = async () => {
        if (!isValid) return;
        setIsSubmitting(true);
        try {
            const tip = await paymentApi.createTip(instructorId, amount);
            navigate(`/tip/${tip.token}`);
        } catch (err) {
            toast.error(err.message || 'Could not start your gift. Please try again.');
            setIsSubmitting(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                title="Help us out"
                icon={mdiHandHeart}
                maxWidth={440}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => navigate('/register')}>Create account</button>
                        <button className="base-modal-save-btn" onClick={() => navigate('/login')}>Log in</button>
                    </>
                }
            >
                <div className="tip-modal">
                    <p className="tip-modal-intro">Log in or create a free account to give a gift.</p>
                </div>
            </BaseModal>
        );
    }

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title="Help us out"
            icon={mdiHandHeart}
            maxWidth={440}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                    <button className="base-modal-save-btn" onClick={handleGive} disabled={!isValid || isSubmitting}>
                        {isSubmitting
                            ? 'Starting...'
                            : isValid ? `Give ${formatCurrencyFromMinor(amount, currency)}` : 'Give a gift'}
                    </button>
                </>
            }
        >
            <div className="tip-modal">
                <p className="tip-modal-intro">Love this app? Choose an amount to help Dance Localé keep growing.</p>
                <div className="tip-modal-chips">
                    {AMOUNT_PRESETS.map(value => (
                        <button
                            key={value}
                            type="button"
                            className={`tip-modal-chip ${preset === value ? 'selected' : ''}`}
                            onClick={() => setPreset(value)}
                        >
                            {formatCurrencyFromMinor(value, currency)}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`tip-modal-chip ${preset === 'other' ? 'selected' : ''}`}
                        onClick={() => setPreset('other')}
                    >
                        Other
                    </button>
                </div>
                {preset === 'other' && (
                    <input
                        type="number"
                        inputMode="decimal"
                        min={MIN_TIP / 100}
                        step="0.01"
                        className="tip-modal-input"
                        placeholder={`Amount (min ${formatCurrencyFromMinor(MIN_TIP, currency)})`}
                        value={custom}
                        onChange={e => setCustom(e.target.value)}
                    />
                )}
            </div>
        </BaseModal>
    );
};

export default TipModal;
